/**
 * Estilos do quadro compartilhado (Compra e Separação). Redesenho da fila do
 * comprador (ADR-0069, 2026-09-14): raios 14 nos cards de página, header de
 * tabela 42px, linha 56px, kanban em grid sem scroll lateral, raia recolhida
 * de 44px. Tudo em tokens `var(--*)` — nada de hex aqui.
 */
import styled, { css } from "styled-components";

import { mq } from "../../../../styles/canonical/breakpoints";
import { getToneTokens } from "../../../../styles/shared";

// ---- Pill de status (tone canônico) ----
export const Pill = styled.span`
  ${({ $tone = "neutral" }) => {
    const t = getToneTokens(null, $tone);
    return css`
      background: ${t.background};
      border: 1px solid ${t.border};
      color: ${t.text};
      --pill-dot: ${t.accent};
    `;
  }}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 26px;
  font-size: 12px;
  font-weight: 600;
  padding: 0 10px;
  border-radius: 999px;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  ${({ $dot }) => $dot && css`
    &::before {
      content: "";
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: var(--pill-dot);
      flex: 0 0 auto;
    }
  `}
`;

/** Tag pequena de origem/tipo (24px, raio 6). */
export const Tag = styled.span`
  ${({ $tone = "neutral" }) => {
    const t = getToneTokens(null, $tone);
    return css`
      background: ${t.background};
      color: ${t.text};
    `;
  }}
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: ${({ $h }) => $h || 24}px;
  padding: 0 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  @media ${mq.mobile} {
    font-size: var(--text-min);
  }
`;

// ---- Barra de filtros ----
export const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  /* Botões utilitários (atualizar, colunas) viram alvos de 40px no celular. */
  @media ${mq.mobile} {
    > .btn { min-height: 40px; height: auto; }
  }
`;

export const SearchWrap = styled.div`
  position: relative;
  flex: 1 1 260px;
  min-width: 180px;
  max-width: 480px;
  display: flex;
  align-items: center;

  /* Auditoria mobile 2026-09-24: a busca dividia a linha com o select e
     ficava com 124px ("Buscar p…"). No celular ocupa a linha inteira. */
  @media ${mq.mobile} {
    flex: 1 1 100%;
    max-width: none;
  }
  > svg { position: absolute; left: 12px; color: var(--ink-4); pointer-events: none; }
  > kbd {
    position: absolute; right: 10px;
    font-family: var(--font-mono); font-size: 11px; color: var(--ink-4);
    border: 1px solid var(--border); border-radius: 4px; padding: 1px 5px;
    background: var(--surface-2);
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  width: 100%;
  min-width: 0;
  height: 36px;
  padding: 0 34px 0 34px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  font: inherit;
  font-size: 13px;
  /* Requisições de Separação usa o input sem o SearchWrap: a regra de
     linha inteira precisa estar aqui também. Altura casa com o ToggleBtn. */
  @media ${mq.mobile} {
    flex: 1 1 100%;
    height: 40px;
  }
  &::placeholder { color: var(--ink-4); }
  &:focus { outline: none; border-color: var(--accent); box-shadow: var(--shadow-focus); }
`;

export const Select = styled.select`
  height: 36px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  font: inherit;
  font-size: 13px;
  @media ${mq.mobile} {
    flex: 1 1 auto;
    min-width: 0;
    height: 40px;
  }
`;

// ---- Toggle de vista ----
export const ViewToggle = styled.div`
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  border-radius: 9px;
  background: var(--surface-2, var(--surface));
  border: 1px solid var(--border);

  /* Auditoria mobile 2026-09-24: quatro vistas não cabem em 375px —
     "Linha do tempo" quebrava em 3 linhas dentro de um botão de 30px e
     "Calendário" saía da tela. Vira faixa rolável, na largura toda. */
  @media ${mq.mobile} {
    flex: 1 1 100%;
    max-width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const ToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  flex: 0 0 auto;
  white-space: nowrap;

  @media ${mq.mobile} {
    height: 40px;
  }
  border: none;
  background: ${({ $on }) => ($on ? "var(--surface)" : "transparent")};
  box-shadow: ${({ $on }) => ($on ? "var(--shadow-1)" : "none")};
  color: ${({ $on }) => ($on ? "var(--ink)" : "var(--ink-3)")};
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 0 12px;
  border-radius: 7px;
  cursor: pointer;
  transition: color .15s ease, background .15s ease;
  &:hover { color: var(--ink); }
`;

/** Chip de filtro/status (28px). `$on` = ativo com accent. */
export const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid ${({ $on }) => ($on ? "var(--accent)" : "var(--border)")};
  background: ${({ $on }) => ($on ? "var(--accent-soft)" : "var(--surface)")};
  color: ${({ $on }) => ($on ? "var(--accent)" : "var(--ink-2)")};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color .15s ease, background .15s ease;
  /* Alvo de toque (S5): 28px no desktop, 36px + 8px de folga no celular. */
  @media ${mq.mobile} {
    height: 40px;
    padding: 0 12px;
    font-size: 13px;
  }
  &:hover { border-color: var(--border-strong); color: var(--ink); }
  > .dot { width: 7px; height: 7px; border-radius: 50%; background: ${({ $cor }) => $cor || "var(--accent)"}; }
  > .n { font-family: var(--font-mono); font-size: 11px; color: var(--ink-3); font-weight: 500; }
  @media ${mq.mobile} { > .n { font-size: var(--text-min); } }
  > .x { display: inline-flex; margin-left: 2px; opacity: .7; }
  > .x:hover { opacity: 1; }
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--accent);
  color: var(--ink-on-accent, #fff);
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 600;
`;

export const IconBtn = styled.button`
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink-2);
  cursor: pointer;
  transition: border-color .15s ease, color .15s ease;
  &:hover { border-color: var(--border-strong); color: var(--ink); }
  @media ${mq.mobile} {
    width: var(--tap-min);
    height: var(--tap-min);
  }
`;

// ---- Tabela ----
export const TableScroll = styled.div`
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--surface);
  box-shadow: var(--shadow-1);
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  th, td {
    padding: 0 12px;
    text-align: left;
    font-size: 13px;
    border-bottom: 1px solid var(--border-subtle);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
  }
  th {
    height: 42px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .06em;
    color: var(--ink-3);
    background: var(--surface-2, var(--surface));
  }
  td { height: 56px; }
  tbody tr { cursor: pointer; transition: background .15s ease; }
  tbody tr:hover td { background: var(--surface-hover); }
  tbody tr[data-selecionada="true"] td { background: var(--accent-soft); }
  tbody tr[data-urgente="true"] td { background: var(--danger-row); }
  tbody tr:last-child td { border-bottom: 0; }
`;

export const CheckCell = styled.td`
  width: 36px;
  text-align: center !important;
  padding: 0 0 0 16px !important;
`;

export const Mono = styled.span`
  font-family: var(--font-mono);
  font-size: ${({ $size }) => $size || 12}px;
  font-weight: 500;
`;

export const SubText = styled.div`
  font-size: 11px;
  color: var(--ink-4);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @media ${mq.mobile} {
    font-size: var(--text-min);
  }
`;

export const TableFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 16px;
  font-size: 12px;
  color: var(--ink-3);
  border-top: 1px solid var(--border-subtle);
  > .spacer { flex: 1; }
`;

export const PageBtn = styled.button`
  height: 28px;
  min-width: 28px;
  padding: 0 8px;
  border-radius: 6px;
  border: 1px solid ${({ $on }) => ($on ? "var(--accent)" : "var(--border)")};
  background: ${({ $on }) => ($on ? "var(--accent-soft)" : "var(--surface)")};
  color: ${({ $on }) => ($on ? "var(--accent)" : "var(--ink-2)")};
  font: inherit;
  font-size: 12px;
  cursor: pointer;
  &:disabled { opacity: .45; cursor: not-allowed; }
  @media ${mq.mobile} {
    height: 36px;
    min-width: 36px;
  }
`;

// ---- Lista em cards (celular) — auditoria mobile 2026-09-24, S2 ----
// "Abaixo de 768px, lista tabular vira card": o dado que a pessoa procura em
// destaque, estado como selo, toque abre o detalhe. Checkbox dentro de uma
// área de toque de 44px (o desenho continua pequeno).
export const CardLista = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const CardItem = styled.div`
  background: var(--surface);
  border: 1px solid ${({ $sel }) => ($sel ? "var(--accent)" : "var(--border)")};
  ${({ $sel }) => ($sel ? "background: var(--accent-soft);" : "")}
  ${({ $urgente }) => ($urgente ? "box-shadow: inset 3px 0 0 var(--danger-solid);" : "")}
  border-radius: 14px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: var(--ink);
  cursor: pointer;
  min-width: 0;
  > .linha { display: flex; align-items: center; gap: 10px; min-width: 0; }
  > .linha > .cresce { flex: 1 1 auto; min-width: 0; }
  > .linha > .fim { margin-left: auto; flex: 0 0 auto; display: inline-flex; align-items: center; gap: 6px; }
  > .titulo { font-weight: 600; font-size: 15px; line-height: 1.3; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  > .sub, > .linha > .sub { font-size: var(--text-min); color: var(--ink-3); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
  > .manchete { font-size: 18px; font-weight: 700; font-variant-numeric: tabular-nums; letter-spacing: -0.01em; line-height: 1.2; }
  > .acoes { display: flex; gap: 8px; align-items: center; padding-top: 2px; }
  > .acoes > .principal { flex: 1 1 auto; justify-content: center; min-height: var(--tap-min); }
`;

/** Área de toque de 44px em volta de um Checkbox de card (o desenho fica menor). */
export const TapCheck = styled.span`
  display: inline-grid;
  place-items: center;
  width: var(--tap-min);
  height: var(--tap-min);
  margin: -11px 0 -11px -11px;
  flex: 0 0 auto;
`;

/** Seletor de raia do kanban no celular: uma raia por vez, chips roláveis. */
export const LaneTabs = styled.div`
  display: flex;
  gap: 6px;
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 2px;
  margin-bottom: 10px;
  &::-webkit-scrollbar { display: none; }
  > button { flex: 0 0 auto; }
`;

// ---- Kanban (grid fluido, sem scroll lateral) ----
export const Board = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols, $recolhidas }) => (
    `repeat(${Math.max(1, $cols)}, minmax(0, 1fr))${$recolhidas ? ` repeat(${$recolhidas}, 44px)` : ""}`
  )};
  gap: 12px;
  align-items: start;
  /* Celular: o Kanban mostra UMA raia por vez (seletor acima). */
  @media ${mq.mobile} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Lane = styled.div`
  min-width: 0;
  background: var(--surface-2, var(--surface));
  border: 1px solid var(--border);
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 420px);
  min-height: 320px;
  ${({ $over }) => $over && css`outline: 2px dashed var(--accent); outline-offset: -2px;`}
  @media ${mq.mobile} {
    max-height: none;
    min-height: 120px;
  }
`;

export const LaneRecolhida = styled.button`
  width: 44px;
  height: 320px;
  border-radius: 14px;
  border: 1px solid var(--border);
  background: var(--surface-2, var(--surface));
  color: var(--ink-3);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 12px 0;
  font: inherit;
  ${({ $over }) => $over && css`outline: 2px dashed var(--accent); outline-offset: -2px;`}
  > .titulo {
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: .04em;
  }
  &:hover { color: var(--ink); border-color: var(--border-strong); }
`;

export const LaneHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid var(--border);
  font-weight: 600;
  font-size: 13px;
  min-width: 0;
  > .titulo { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  > .hint { margin-left: auto; font-size: 11px; font-weight: 500; color: var(--ink-4); white-space: nowrap; }
`;

export const LaneDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $cor }) => $cor || "var(--neutral-solid)"};
  flex: 0 0 auto;
`;

export const LaneCount = styled.span`
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  color: var(--ink-3);
  @media ${mq.mobile} { font-size: var(--text-min); }
`;

export const LaneBody = styled.div`
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  min-height: 60px;
  scrollbar-width: thin;
  scrollbar-color: var(--border-strong) transparent;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: var(--border-strong); border-radius: 6px; }
`;

export const KCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-left: 3px solid ${({ $cor }) => $cor || "var(--border)"};
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: var(--shadow-1);
  cursor: grab;
  min-width: 0;
  transition: border-color .15s ease, box-shadow .15s ease;
  &:active { cursor: grabbing; }
  &:hover { border-color: var(--border-strong); box-shadow: var(--shadow-2); }
  &[data-selecionado="true"] { border-color: var(--accent); box-shadow: 0 0 0 1px var(--accent); }
  /* Caixa de seleção: recolhida até o hover/foco do card; marcada, fica. */
  .ksel {
    display: inline-flex;
    width: 0;
    opacity: 0;
    overflow: hidden;
    transition: width .14s ease, opacity .14s ease;
  }
  &:hover .ksel,
  &:focus-within .ksel,
  .ksel[data-on] {
    width: 18px;
    opacity: 1;
    overflow: visible;
  }
  @media (hover: none) {
    .ksel { width: 18px; opacity: 1; overflow: visible; }
  }
`;

export const KNum = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--ink-3);
  min-width: 0;
  @media ${mq.mobile} {
    font-size: var(--text-min);
  }
`;

export const KTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const KMeta = styled.div`
  font-size: 11px;
  color: var(--ink-3);
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  > .spacer { flex: 1; }
  @media ${mq.mobile} {
    font-size: var(--text-min);
  }
`;

// ---- Barra de ações em lote (flutuante, acompanha o tema) ----
export const BatchBar = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 8px 8px 16px;
  border-radius: 12px;
  background: var(--bg-elevated);
  border: 1px solid var(--border-strong);
  color: var(--ink);
  box-shadow: var(--shadow-4);
  z-index: 20;
  flex-wrap: wrap;
  max-width: calc(100vw - 32px);
  > .sep { width: 1px; height: 22px; background: var(--border); margin: 0 4px; }
  /* Celular: encosta nas bordas, respeita a barra de gestos e os botões
     viram alvos de 40px (auditoria mobile 2026-09-24, S5). */
  @media ${mq.mobile} {
    left: 16px;
    right: 16px;
    transform: none;
    bottom: calc(16px + var(--safe-area-bottom, 0px));
    max-width: none;
    justify-content: center;
    padding: 10px;
    > .sep { display: none; }
    > .btn { min-height: 40px; height: auto; }
  }
`;

// ---- Linha do tempo (Separação continua usando) ----
export const TimelineDay = styled.div`
  margin-bottom: 18px;
`;
export const TimelineDate = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: var(--ink-2);
  text-transform: uppercase;
  letter-spacing: .05em;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--border);
  margin-bottom: 8px;
`;
export const TimelineItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 6px;
  cursor: pointer;
  font: inherit;
  color: var(--ink);
  &:hover { border-color: var(--border-strong); background: var(--surface-hover); }
  @media ${mq.mobile} {
    min-height: 56px;
  }
`;

// ---- Calendário ----
export const CalWrap = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: var(--shadow-1);
  padding: 14px 16px 16px;
`;
export const CalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  overflow: hidden;
`;
export const CalHead = styled.div`
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: var(--ink-3);
  text-transform: uppercase;
  letter-spacing: .06em;
  padding: 8px 0;
  background: var(--surface-2);
  border-bottom: 1px solid var(--border-subtle);
`;
export const CalCell = styled.div`
  min-height: 104px;
  border-right: 1px solid var(--border-subtle);
  border-bottom: 1px solid var(--border-subtle);
  padding: 8px;
  /* Celular: célula de 48px com o chip reduzido ao número (o nome não
     cabe em 7 colunas de 47px; o toque abre o detalhe). */
  @media ${mq.mobile} {
    min-height: 64px;
    padding: 4px 3px;
    gap: 2px;
  }
  background: ${({ $muted }) => ($muted ? "var(--surface-2)" : "var(--surface)")};
  color: ${({ $muted }) => ($muted ? "var(--ink-4)" : "var(--ink)")};
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  &:nth-child(7n) { border-right: 0; }
`;
export const CalDay = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $hoje }) => ($hoje ? "#fff" : "inherit")};
  background: ${({ $hoje }) => ($hoje ? "var(--accent)" : "transparent")};
`;
export const CalChip = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  width: 100%;
  height: 20px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  border: none;
  border-left: 2px solid ${({ $cor }) => $cor || "var(--border)"};
  background: ${({ $bg }) => $bg || "var(--surface-2)"};
  color: var(--ink);
  border-radius: 4px;
  padding: 0 6px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  > .num { font-family: var(--font-mono); font-weight: 500; color: var(--ink-3); flex: 0 0 auto; }
  > .nome { overflow: hidden; text-overflow: ellipsis; }
  &:hover { filter: brightness(0.97); }
  @media ${mq.mobile} {
    height: 22px;
    padding: 0 3px;
    gap: 0;
    > .nome { display: none; }
  }
`;

// ---- Detalhe (slide-over) ----
export const DField = styled.div`
  margin-bottom: 12px;
  b { display: block; font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: var(--ink-3); margin-bottom: 2px; font-weight: 600; }
  span { font-size: 13px; color: var(--ink); }
`;

// ---- Checklist de separacao (detalhe) ----
export const ProgressoWrap = styled.div`
  margin-bottom: 14px;
`;

export const ProgressoTexto = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 700;
  color: var(--ink-2);
  margin-bottom: 5px;
`;

export const ProgressoBarra = styled.div`
  height: 8px;
  border-radius: 999px;
  background: var(--surface-2, rgba(0,0,0,.06));
  border: 1px solid var(--border);
  overflow: hidden;
  > div {
    height: 100%;
    width: ${({ $pct }) => Math.min(100, Math.max(0, $pct))}%;
    background: ${({ $pct }) => ($pct >= 100 ? "var(--success-solid)" : "var(--accent)")};
    transition: width .25s ease;
  }
`;

export const ChkAmbiente = styled.div`
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: .05em;
  color: var(--ink-2);
  margin: 14px 0 6px;
  padding-bottom: 4px;
  border-bottom: 1px solid var(--border);
`;

export const ChkModulo = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: var(--ink-3);
  margin: 8px 0 4px;
`;

export const ChkRow = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  text-align: left;
  font: inherit;
  background: ${({ $on }) => ($on ? "var(--success-bg)" : "var(--surface)")};
  border: 1px solid ${({ $on }) => ($on ? "var(--success-border)" : "var(--border)")};
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 6px;
  cursor: pointer;
  &:hover { border-color: ${({ $on }) => ($on ? "var(--success-solid)" : "var(--accent)")}; }
`;

export const ChkBox = styled.span`
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  border-radius: 5px;
  border: 1.5px solid ${({ $on }) => ($on ? "var(--success-solid)" : "var(--border)")};
  background: ${({ $on }) => ($on ? "var(--success-solid)" : "var(--surface)")};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export const ChkNome = styled.div`
  min-width: 0;
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  color: ${({ $on }) => ($on ? "var(--ink-2)" : "var(--ink)")};
  text-decoration: ${({ $on }) => ($on ? "line-through" : "none")};
  span { display: block; font-size: 11px; color: var(--ink-3); text-decoration: none; }
`;

export const ChkQt = styled.div`
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 700;
  color: ${({ $on }) => ($on ? "var(--success-text)" : "var(--ink)")};
`;

export const EmptyBox = styled.div`
  text-align: center;
  color: var(--ink-3);
  padding: 40px 0;
  border: 1px dashed var(--border-strong);
  border-radius: 14px;
`;
